import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from "@mui/material";

export default function DomainChecker() {
  const [inputDomains, setInputDomains] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isValidDomain = (domain) => {
  // Izinkan domain dengan TLD berlapis seperti .co.id, .com.br, dll
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domain.trim());
  };

  const handleCheck = async () => {
    setError("");
    setResults([]);

    const rawDomains = inputDomains
      .split("\n")
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d !== "");

    if (rawDomains.length === 0) {
      setError("Masukkan minimal satu domain.");
      return;
    }

    if (rawDomains.length > 100) {
      setError("Maksimal pengecekan 100 domain.");
      return;
    }

    for (const d of rawDomains) {
      if (!isValidDomain(d)) {
        setError(`Domain tidak valid: ${d}`);
        return;
      }
    }

    setLoading(true);
    try {
      const query = encodeURIComponent(rawDomains.join(","));
      const res = await fetch(`https://check.skiddle.id/?domains=${query}`);
      const data = await res.json();

      if (typeof data === "object" && !Array.isArray(data)) {
        const mappedResults = Object.entries(data).map(([domain, value]) => ({
          domain,
          status: value?.blocked === true ? "Ada" : "Tidak Ada",
        }));
        setResults(mappedResults);
        return;
      }

      if (data.domains && Array.isArray(data.domains)) {
        const mappedResults = rawDomains.map((domain) => {
          const found = data.domains.find((item) => item.domain === domain);
          return {
            domain,
            status: found?.blocked === true ? "Ada" : "Tidak Ada",
          };
        });
        setResults(mappedResults);
        return;
      }

      throw new Error("Format respons API tidak valid.");
    } catch (err) {
      setError("Gagal mengecek domain: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      maxWidth={700}
      mx="auto"
      mt={5}
      p={4}
      component={Paper}
      elevation={8}
      borderRadius={3}
      sx={{
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
      }}
    >
      <Typography variant="h4" mb={3} fontWeight="bold" textAlign="center">
        Domain Checker Kominfo
      </Typography>

      <TextField
        label="Masukkan Domain (pisahkan dengan baris baru) Max 100 Baris"
        placeholder="contoh: domain1.com\ndomain2.net"
        multiline
        minRows={6}
        fullWidth
        value={inputDomains}
        onChange={(e) => setInputDomains(e.target.value)}
        disabled={loading}
        sx={{
          bgcolor: "white",
          borderRadius: 1,
          mb: 2,
        }}
      />

      <Box display="flex" justifyContent="center" mb={2}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleCheck}
          disabled={loading}
          size="large"
          sx={{
            px: 5,
            fontWeight: "bold",
            textTransform: "uppercase",
            boxShadow: "0 3px 10px rgba(0,0,0,0.3)",
            "&:hover": {
              backgroundColor: "#5a379e",
              boxShadow: "0 6px 15px rgba(0,0,0,0.4)",
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress
                size={24}
                sx={{ color: "white", mr: 1 }}
              />
              Memeriksa...
            </>
          ) : (
            "Cek Domain"
          )}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontWeight: "bold" }}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{ maxHeight: 300, overflowY: "auto", bgcolor: "white" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ fontWeight: "bold", backgroundColor: "#764ba2", color: "white" }}
              >
                Domain
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", backgroundColor: "#764ba2", color: "white" }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ fontStyle: "italic" }}>
                  Tidak ada hasil
                </TableCell>
              </TableRow>
            ) : (
              results.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": { backgroundColor: "#e0e0e0" },
                    cursor: "default",
                  }}
                >
                  <TableCell>{item.domain}</TableCell>
                  <TableCell
                    sx={{
                      color: item.status === "Ada" ? "#e53935" : "#43a047",
                      fontWeight: "bold",
                    }}
                  >
                    {item.status}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
